import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trophy, CalendarDays, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { useMapAllocations, useMapBuildings, useLocationSchedules, usePrefetchLocationSchedules } from '@/hooks'
import { useTodayBoardData } from '@/components/today-board/useTodayBoardData'
import { CompactHeader } from '@/components/today-board/CompactHeader'
import { DayStrip } from '@/components/today-board/DayStrip'
import { DatePickerButton } from '@/components/today-board/DatePickerButton'
import { ViewModeToggle } from '@/components/today-board/ViewModeToggle'
import { AttentionBanner } from '@/components/today-board/AttentionBanner'
import { BoardColumns } from '@/components/today-board/BoardColumns'
import { BoardTabs } from '@/components/today-board/BoardTabs'
import { QuickActionsBar } from '@/components/today-board/QuickActionsBar'
import { AddPlaneWizard } from '@/components/ramp-wizard/AddPlaneWizard'
import { ScheduleEditDialog } from '@/components/ramp-wizard/ScheduleEditDialog'
import { DepartureDialog } from '@/components/ramp-wizard/DepartureDialog'
import { QuickDepartureDialog } from '@/components/today-board/QuickDepartureDialog'
import { ArrivalDialog } from '@/components/ramp-wizard/ArrivalDialog'
import { ReservationDetailsDialog } from '@/components/today-board/ReservationDetailsDialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useAddPlaneWizardStore } from '@/stores/addPlaneWizardStore'
import type { ViewMode } from '@/components/today-board/MovementsArea'
import type { ScheduleItem } from '@/components/today-board/useTodayBoardData'

function isToday(date: Date): boolean {
  const now = new Date()
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

export function TodayBoardPage() {
  const { locationId, locationSlug } = useAppContext()
  const navigate = useNavigate()
  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('list')

  // Demo convenience: jump straight to the Masters week at KAGS (Thu Apr 8 2027 — peak arrivals).
  const jumpToMasters = useCallback(() => {
    setSelectedDate(new Date(2027, 3, 8, 12, 0, 0))
    if (locationSlug !== 'kags') navigate('/kags/schedule')
  }, [locationSlug, navigate])

  const jumpToToday = useCallback(() => {
    setSelectedDate(new Date())
  }, [])

  // Wizard store for prefill-from-schedule opens
  const openWizard = useAddPlaneWizardStore((s) => s.openWizard)

  // Data — reuses same TanStack Query as Ramp View
  const { enrichedData, isLoading } = useMapAllocations(locationId)
  const { buildings } = useMapBuildings(locationId)
  const { data: locationSchedules, isFetching: schedulesFetching } = useLocationSchedules(locationId, selectedDate)

  // Warm the cache so day-to-day toggling is instant: the visible strip window
  // (selected ±3..4) plus the next few days ahead.
  const prefetchSchedules = usePrefetchLocationSchedules(locationId)
  useEffect(() => {
    const around: Date[] = []
    for (let offset = -4; offset <= 4; offset++) {
      const d = new Date(selectedDate)
      d.setDate(d.getDate() + offset)
      around.push(d)
    }
    prefetchSchedules(around)
  }, [selectedDate, prefetchSchedules])

  // Compute total units from buildings
  const totalUnits = useMemo(() => {
    let count = 0
    for (const area of buildings) {
      count += area.generatedUnits?.length || 0
    }
    return count
  }, [buildings])

  // Transform data for board
  const boardData = useTodayBoardData(enrichedData, selectedDate, totalUnits, locationSchedules)
  const today = isToday(selectedDate)

  // ── Dialog state ──
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)
  const [scheduleDialogAircraftId, setScheduleDialogAircraftId] = useState<string | null>(null)
  const [scheduleDialogEditSchedule, setScheduleDialogEditSchedule] = useState<ScheduleItem['scheduleId'] | null>(null)

  // Create-mode schedule dialog (from footer "+ Add Schedule")
  const [createScheduleDialogOpen, setCreateScheduleDialogOpen] = useState(false)

  const [departureDialogOpen, setDepartureDialogOpen] = useState(false)
  const [departureDialogItem, setDepartureDialogItem] = useState<ScheduleItem | null>(null)

  // Quick departure dialog (for scheduled departures)
  const [quickDepartureOpen, setQuickDepartureOpen] = useState(false)
  const [quickDepartureItem, setQuickDepartureItem] = useState<ScheduleItem | null>(null)

  const [arrivalDialogOpen, setArrivalDialogOpen] = useState(false)
  const [arrivalDialogItem, setArrivalDialogItem] = useState<ScheduleItem | null>(null)

  // Reservation details modal (full FlightBridge info)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsItem, setDetailsItem] = useState<ScheduleItem | null>(null)

  // Resolve schedule object for edit mode from the fetched locationSchedules
  const scheduleForEdit = useMemo(() => {
    if (!scheduleDialogEditSchedule || !locationSchedules) return null
    return locationSchedules.find((s) => s.id === scheduleDialogEditSchedule) ?? null
  }, [scheduleDialogEditSchedule, locationSchedules])

  const handleRowClick = useCallback((item: ScheduleItem) => {
    setScheduleDialogAircraftId(item.data.aircraft.id)
    setScheduleDialogEditSchedule(item.scheduleId ?? null)
    setScheduleDialogOpen(true)
  }, [])

  const handleCheckIn = useCallback((item: ScheduleItem) => {
    setArrivalDialogItem(item)
    setArrivalDialogOpen(true)
  }, [])

  const handleShowDetails = useCallback((item: ScheduleItem) => {
    setDetailsItem(item)
    setDetailsOpen(true)
  }, [])

  const handleRegister = useCallback((item: ScheduleItem) => {
    const aircraft = item.data.aircraft
    const owner = item.owner

    // Clear any residual wizard state, then open fresh from the ramp.
    const store = useAddPlaneWizardStore.getState()
    store.reset()
    openWizard({
      source: 'ramp',
      locationId: locationId ?? undefined,
    })

    // ── Aircraft (step 0): prefill existing aircraft; ownerClientId auto-selects the client ──
    store.setSelectedExistingAircraft(
      aircraft.id,
      aircraft.tailNumber,
      aircraft.wingspan || aircraft.length ? {
        wingspan: aircraft.wingspan,
        length: aircraft.length,
        height: aircraft.height,
        tail_height: aircraft.tailHeight,
      } : null,
      aircraft.typeCode,
      owner?.id ?? null, // 5th arg — auto-select the existing owner client
      aircraft.defaultFuelType ?? undefined,
    )

    // ── Client (step 1): if the owner isn't a known client, prefill a new one from the feed ──
    if (!owner?.id && owner) {
      store.toggleCreateNewClient(true)
      store.setNewClientData({
        client_type: owner.clientType === 'individual' ? 'individual' : 'corporate',
        company_name: owner.companyName ?? undefined,
        first_name: owner.firstName ?? undefined,
        last_name: owner.lastName ?? undefined,
        email_primary: owner.emailPrimary ?? undefined,
        phone_primary: owner.phonePrimary ?? undefined,
      })
      const c = owner.primaryContact
      if (c) {
        store.setNewContactData({
          first_name: c.firstName ?? '',
          last_name: c.lastName ?? '',
          email_primary: c.emailPrimary ?? '',
          phone_mobile: c.phoneMobile ?? undefined,
        })
      }
    }

    // ── Contract (step 2): transient w/ schedule dates, or link an existing lease if one exists ──
    const arrivalTime = item.movementType === 'arriving' ? item.scheduledTime : item.pairedScheduledTime
    const departureTime = item.movementType === 'arriving' ? item.pairedScheduledTime : item.scheduledTime
    store.seedContractFromSchedule({
      checkIn: arrivalTime ?? null,
      checkOut: departureTime ?? null,
      // Register only shows when the aircraft isn't already set up, so an active lease is rare,
      // but if the enriched data carries one, link it instead of creating a transient stay.
      leaseContractId: item.data.leaseContract?.id ?? null,
    })
  }, [locationId, openWizard])

  const handleCheckOut = useCallback((item: ScheduleItem) => {
    // Scenario A: Scheduled departure (has scheduleId) → quick confirm
    if (item.scheduleId) {
      setQuickDepartureItem(item)
      setQuickDepartureOpen(true)
      return
    }
    // Scenario B: Unscheduled departure → full departure dialog
    setDepartureDialogItem(item)
    setDepartureDialogOpen(true)
  }, [])

  if (!locationId) {
    return (
      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
        Select a location to view today&apos;s board
      </div>
    )
  }

  // Only the initial allocations/buildings load blocks the whole page. Schedule
  // refetches on date change are surfaced inline in the events area (below) so the
  // header/strip stay mounted and switching days never greys out the page.
  if (isLoading) {
    return (
      <div className="flex flex-col h-full p-4 gap-3">
        <Skeleton className="h-12 w-full shrink-0" />
        <Skeleton className="h-8 w-full shrink-0" />
        <Skeleton className="h-9 w-full shrink-0" />
        <Skeleton className="flex-1" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Compact header: live clock + counter pills */}
      <div className="shrink-0">
        <CompactHeader selectedDate={selectedDate} counters={boardData.counters} />
      </div>

      {/* Day navigation strip — Masters/Today shortcuts left, date picker + view toggle right */}
      <div className="shrink-0">
        <DayStrip
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          leftSlot={
            <div className="flex items-center gap-2">
              {locationSlug === 'kags' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs"
                  onClick={jumpToMasters}
                  title="Jump to Masters week at KAGS"
                >
                  <Trophy className="h-3.5 w-3.5 text-amber-500" />
                  Masters Event
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={jumpToToday}
                title="Jump to today"
              >
                <CalendarDays className="h-3.5 w-3.5 text-primary" />
                Today
              </Button>
            </div>
          }
        >
          <div className="flex items-center gap-2">
            <DatePickerButton selectedDate={selectedDate} onDateChange={setSelectedDate} />
            <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </DayStrip>
      </div>

      {/* Attention banner */}
      <div className="shrink-0 py-1">
        <AttentionBanner
          items={boardData.needsAttention}
          severitySummary={boardData.severitySummary}
          isToday={today}
        />
      </div>

      {/* Scrollable body — full-width movements. Only this area dims while a new
          day's schedules load (header/strip stay interactive). */}
      <div className="relative flex-1 min-h-0 p-4 pt-2">
        {schedulesFetching && (
          <div className="pointer-events-none absolute right-5 top-3 z-10 flex items-center gap-1.5 rounded-full bg-background/90 px-2 py-0.5 text-[11px] text-muted-foreground shadow-sm">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        )}
        <div className={`h-full transition-opacity ${schedulesFetching ? 'opacity-60' : 'opacity-100'}`}>
          {/* Desktop */}
          <div className="hidden lg:block h-full">
            <BoardColumns
              data={boardData}
              isToday={today}
              viewMode={viewMode}
              onRowClick={handleRowClick}
              onCheckIn={handleCheckIn}
              onCheckOut={handleCheckOut}
              onRegister={handleRegister}
              onShowDetails={handleShowDetails}
            />
          </div>

          {/* Mobile/Tablet: tabbed layout */}
          <div className="lg:hidden h-full">
            <BoardTabs data={boardData} isToday={today} viewMode={viewMode} onRowClick={handleRowClick} onCheckIn={handleCheckIn} onCheckOut={handleCheckOut} onRegister={handleRegister} onShowDetails={handleShowDetails} />
          </div>
        </div>
      </div>

      {/* Sticky bottom quick actions */}
      <div className="shrink-0">
        <QuickActionsBar
          locationSlug={locationSlug}
          locationId={locationId}
          onAddSchedule={() => setCreateScheduleDialogOpen(true)}
        />
      </div>

      {/* Schedule Edit Dialog (create or edit mode — row click) */}
      <ScheduleEditDialog
        open={scheduleDialogOpen}
        onOpenChange={(open) => {
          setScheduleDialogOpen(open)
          if (!open) setScheduleDialogEditSchedule(null)
        }}
        schedule={scheduleForEdit}
        aircraftId={scheduleDialogAircraftId ?? undefined}
        locationId={locationId ?? undefined}
      />

      {/* Schedule Create Dialog (from footer "+ Add Schedule") */}
      <ScheduleEditDialog
        open={createScheduleDialogOpen}
        onOpenChange={setCreateScheduleDialogOpen}
        schedule={null}
        locationId={locationId ?? undefined}
      />

      {/* Departure Dialog — full form (unscheduled departures) */}
      {departureDialogItem && locationId && (
        <DepartureDialog
          open={departureDialogOpen}
          onOpenChange={(open) => { setDepartureDialogOpen(open); if (!open) setDepartureDialogItem(null) }}
          tailNumber={departureDialogItem.data.aircraft.tailNumber}
          allocationId={departureDialogItem.allocationId}
          aircraftId={departureDialogItem.data.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Quick Departure Dialog — simple confirm (scheduled departures) */}
      {quickDepartureItem && locationId && (
        <QuickDepartureDialog
          open={quickDepartureOpen}
          onOpenChange={(open) => { setQuickDepartureOpen(open); if (!open) setQuickDepartureItem(null) }}
          item={quickDepartureItem}
          locationId={locationId}
        />
      )}

      {/* Arrival Dialog (Check In) */}
      {arrivalDialogItem && locationId && (
        <ArrivalDialog
          open={arrivalDialogOpen}
          onOpenChange={(open) => { setArrivalDialogOpen(open); if (!open) setArrivalDialogItem(null) }}
          tailNumber={arrivalDialogItem.data.aircraft.tailNumber}
          allocationId={arrivalDialogItem.allocationId}
          aircraftId={arrivalDialogItem.data.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Reservation details modal (full FlightBridge info) */}
      <ReservationDetailsDialog
        item={detailsItem}
        open={detailsOpen}
        onOpenChange={(open) => { setDetailsOpen(open); if (!open) setDetailsItem(null) }}
        onRegister={handleRegister}
        canRegister={
          detailsItem?.movementType === 'arriving' &&
          !(detailsItem.allocationId && (detailsItem.data.leaseContract || detailsItem.data.transientStay))
        }
      />

      {/* Add Plane Wizard (required for Register + Walk-In buttons) */}
      <AddPlaneWizard locationId={locationId ?? undefined} />
    </div>
  )
}
